import { useEffect, useState } from 'react';
import { FiDownload, FiShare, FiSmartphone, FiCheckCircle } from 'react-icons/fi';
import { Modal } from './Modals';
import {
  INSTALL_STATE_EVENT,
  hasNativeInstallPrompt,
  isIOSDevice,
  isIPad,
  isIOSSafari,
  isPwaInstalled,
  launchNativeInstallPrompt
} from '../utils/pwaInstall';

interface InstallAppProps {
  variant?: 'topbar' | 'login';
}

/**
 * Shared between the unauthenticated login and the authenticated top bar.
 * Never requires access to invoice data or any backend credentials.
 */
export const InstallApp = ({ variant = 'topbar' }: InstallAppProps) => {
  const [installed, setInstalled] = useState(isPwaInstalled);
  const [hasPrompt, setHasPrompt] = useState(hasNativeInstallPrompt);
  const [showGuide, setShowGuide] = useState(false);
  const [isPrompting, setIsPrompting] = useState(false);
  const ios = isIOSDevice();
  const ipad = isIPad();
  const safari = isIOSSafari();

  useEffect(() => {
    const sync = () => {
      setInstalled(isPwaInstalled());
      setHasPrompt(hasNativeInstallPrompt());
    };
    const display = window.matchMedia('(display-mode: standalone)');
    window.addEventListener(INSTALL_STATE_EVENT, sync);
    display.addEventListener?.('change', sync);
    window.addEventListener('pageshow', sync);
    sync();
    return () => {
      window.removeEventListener(INSTALL_STATE_EVENT, sync);
      display.removeEventListener?.('change', sync);
      window.removeEventListener('pageshow', sync);
    };
  }, []);

  const handleInstallClick = () => {
    if (installed || isPrompting) return;
    if (!hasPrompt) {
      setShowGuide(true);
      return;
    }
    // The native prompt is invoked synchronously inside the click handler.
    setIsPrompting(true);
    void launchNativeInstallPrompt().then(result => {
      setIsPrompting(false);
      if (result === 'unavailable') setShowGuide(true);
    });
  };

  if (installed) return null;

  const label = ios ? 'Get app' : 'Install app';
  const topbar = variant === 'topbar';

  return (
    <>
      <button
        type="button"
        onClick={handleInstallClick}
        disabled={isPrompting}
        aria-label={ios ? 'Get InvoiceFlow as an app: installation instructions' : 'Install InvoiceFlow Studio'}
        title={label}
        className={
          topbar
            ? 'inline-flex h-11 min-h-11 min-w-11 shrink-0 items-center justify-center gap-2 rounded-xl border border-teal-200 bg-teal-50 px-2 text-teal-800 transition-colors hover:bg-teal-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-600 disabled:opacity-60 sm:px-3'
            : 'inline-flex h-11 min-h-11 min-w-11 shrink-0 items-center justify-center gap-1.5 rounded-xl border border-white/60 bg-white/15 px-2 text-white shadow-sm backdrop-blur-sm transition-colors hover:bg-white/25 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white disabled:opacity-60 sm:px-3'
        }
      >
        <FiDownload className="h-[19px] w-[19px] shrink-0" aria-hidden="true" />
        <span className={topbar ? 'hidden text-xs font-bold lg:inline' : 'hidden text-xs font-bold min-[380px]:inline'}>
          {isPrompting ? 'Opening…' : label}
        </span>
      </button>

      <Modal
        isOpen={showGuide}
        onClose={() => setShowGuide(false)}
        title="Get InvoiceFlow as an app"
        size="md"
        footer={
          <button
            type="button"
            onClick={() => setShowGuide(false)}
            className="min-h-11 w-full rounded-xl bg-teal-700 px-4 py-2.5 text-sm font-bold text-white hover:bg-teal-800 sm:w-auto"
          >
            Got it
          </button>
        }
      >
        <div className="space-y-4 text-sm leading-relaxed text-slate-700">
          <div className="flex items-start gap-3 rounded-xl border border-teal-100 bg-teal-50 p-3">
            <FiSmartphone className="mt-0.5 h-5 w-5 shrink-0 text-teal-700" aria-hidden="true" />
            <p>
              Keep InvoiceFlow on your home screen for quick, app-like access. Your existing
              account and permissions still apply; sign in as usual.
            </p>
          </div>
          {ios ? (
            <>
              {!safari && (
                <p className="rounded-lg bg-amber-50 p-3 text-amber-900">
                  For reliable installation, open this page in Safari first. Some iOS browsers
                  also offer Add to Home Screen through their own Share menu.
                </p>
              )}
              <p className="font-semibold text-slate-900">
                {ipad ? 'On your iPad in Safari:' : 'On your iPhone in Safari:'}
              </p>
              <ol className="list-decimal space-y-3 pl-5 marker:font-bold marker:text-teal-700">
                <li>
                  <span className="inline-flex items-center gap-1">
                    Tap <FiShare className="inline h-4 w-4 text-teal-700" aria-label="Share" />
                    <strong>Share</strong> in Safari's {ipad ? 'toolbar' : 'toolbar'}.
                  </span>
                  {' '}On newer Safari layouts, first open the Page Menu, then choose Share.
                </li>
                <li>Scroll down and tap <strong>Add to Home Screen</strong>. If it is missing, scroll to the bottom and choose Edit Actions to add it.</li>
                <li>If shown, keep <strong>Open as Web App</strong> enabled, then tap <strong>Add</strong>.</li>
              </ol>
              <p className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
                iOS does not permit websites to install themselves with one click.
                The Home Screen icon is added through Safari.
              </p>
            </>
          ) : (
            <>
              <p className="font-semibold text-slate-900">Install from your browser</p>
              <ol className="list-decimal space-y-3 pl-5 marker:font-bold marker:text-teal-700">
                <li>Open your browser's menu, or look for the installation icon in the address bar.</li>
                <li>Select <strong>Install app</strong>, <strong>Add to Home screen</strong>, or the equivalent option if available.</li>
                <li>Confirm the browser's installation request. If no install option is offered, you can still bookmark this page.</li>
              </ol>
              <p className="text-xs text-slate-500">
                Browser installation options vary. Chrome and Edge show a native prompt when the browser considers the site installable.
              </p>
            </>
          )}
          <div className="flex items-center gap-2 border-t border-slate-100 pt-3 text-xs text-slate-600">
            <FiCheckCircle className="h-4 w-4 shrink-0 text-teal-700" aria-hidden="true" />
            Installing does not save invoices or financial records for offline access.
          </div>
        </div>
      </Modal>
    </>
  );
};